import React from 'react';

import { Pressable, StyleSheet, View } from 'react-native';
import { Icon } from 'ucl/components/icon/Icon';

import { Typography } from 'components/ui';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';

type UnavailableOfferFooterProps = {
  message?: string;
  onPress?: () => void;
};

/**
 * White footer bar for unavailable offer cards.
 * When `onPress` is set, renders the “Tap here to choose another restaurant” CTA.
 * Otherwise shows the provided unavailable message.
 */
export function UnavailableOfferFooter({ message, onPress }: UnavailableOfferFooterProps) {
  if (!message && !onPress) {
    return null;
  }

  const content = onPress ? (
    <Typography variant="copyTwo" color={primitive.$bbqBrown} style={styles.message}>
      <Typography variant="copyTwo" color={primitive.$bbqBrown} style={styles.tapHere}>
        {t('tapHere')}
      </Typography>
      {` ${t('toChooseAnotherRestaurant')}`}
    </Typography>
  ) : (
    <Typography variant="copyTwo" color={primitive.$bbqBrown} style={styles.message}>
      {message}
    </Typography>
  );

  if (onPress) {
    return (
      <Pressable
        accessibilityRole="button"
        onPress={onPress}
        style={styles.footer}
        testID="unavailable-offer-footer"
      >
        <Icon variant="info" size="4" color={primitive.$bbqBrown} mr="$2" />
        {content}
      </Pressable>
    );
  }

  return (
    <View style={styles.footer} testID="unavailable-offer-footer">
      <Icon variant="info" size="4" color={primitive.$bbqBrown} mr="$2" />
      {content}
    </View>
  );
}

const styles = StyleSheet.create({
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: primitive.$white,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    borderCurve: 'continuous',
    paddingHorizontal: spacing.$4,
    paddingVertical: spacing.$3,
  },
  message: {
    flexShrink: 1,
    fontSize: 13,
  },
  tapHere: {
    fontSize: 13,
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
});
